/**
 * Create form for an assessment.
 */
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Building2, UserCheck, X } from 'lucide-react';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { Select } from '@/components/forms/Select';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useAssignmentEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { useUsersSearch } from '@/hooks/useUsersSearch';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel } from '@/utils/role-label-utils';
import { createAssessment } from '@/services/assessments.service';
import type { AssessmentType } from '@/types/assessment.types';
import { ASSESSMENT_FORM_QUERY_KEYS, ASSESSMENT_TYPE_OPTIONS } from './assessment-configs';
import {
  addAssessmentTargetUser,
  buildCreateAssessmentDto,
  getAssessmentDefinition,
  getCreateAssessmentByType,
  isCreateAssessmentValid,
  mapAssessmentUserOptions,
  removeAssessmentTargetUser,
  scopedPersonPlaceholder,
  formatCreateAssessmentSuccess,
} from './assessment-form-utils';

/** Props for {@link CreateAssessmentView}. */
interface CreateAssessmentViewProps {
  onClose: () => void;
  onSuccess?: () => void;
  className?: string;
}

/** Create form for an assessment. */
export const CreateAssessmentView: React.FC<CreateAssessmentViewProps> = ({
  onClose,
  onSuccess,
  className,
}) => {
  const queryClient = useQueryClient();
  const [type, setType] = React.useState<AssessmentType | ''>('');
  const [establishmentId, setEstablishmentId] = React.useState('');
  const [targetUserId, setTargetUserId] = React.useState('');
  const [targetUsers, setTargetUsers] = React.useState<{ id: string; label: string }[]>([]);
  const [coordinatorUserId, setCoordinatorUserId] = React.useState('');

  const def = getAssessmentDefinition(type);
  const byType = getCreateAssessmentByType(type);
  const establishmentChosen = Boolean(establishmentId);

  const {
    options: establishmentOptions,
    isLoading: establishmentLoading,
    onSearch: onEstablishmentSearch,
  } = useAssignmentEstablishmentsAutocomplete();

  /** Changing type or establishment drops outdated person ids. */
  const resetPersonSelections = () => {
    setTargetUserId('');
    setTargetUsers([]);
    setCoordinatorUserId('');
  };

  const {
    users: vscUsers,
    isLoading: vscLoading,
    onSearch: onVscSearch,
  } = useUsersSearch({
    role: 'volunteer',
    excludedIds: targetUsers.map((user) => user.id),
    filters: { establishmentId },
    enabled: establishmentChosen,
  });

  const {
    users: coordinatorUsers,
    isLoading: coordinatorLoading,
    onSearch: onCoordinatorSearch,
  } = useUsersSearch({
    role: 'coordinator',
    filters: { establishmentId },
    enabled: establishmentChosen,
  });

  const vscOptions = React.useMemo(() => mapAssessmentUserOptions(vscUsers), [vscUsers]);
  const coordinatorOptions = React.useMemo(
    () => mapAssessmentUserOptions(coordinatorUsers),
    [coordinatorUsers],
  );

  const createMutation = useMutation({
    mutationFn: () =>
      createAssessment(
        buildCreateAssessmentDto({
          type,
          definition: def,
          establishmentId,
          targetUserId,
          targetUsers,
          coordinatorUserId,
        }),
      ),
    onSuccess: async (created) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ASSESSMENT_FORM_QUERY_KEYS.list,
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: ['assessment'],
          refetchType: 'all',
        }),
      ]);
      toast.success(formatCreateAssessmentSuccess(created.length));
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  let formTitle = 'Créer un bilan';
  if (type && type !== 'other' && def) {
    formTitle = `Créer un ${def.label.toLowerCase()}`;
  }
  const formSubtitle = byType?.subtitle ?? 'Renseignez les informations du bilan';

  const isValid = React.useMemo(
    () =>
      isCreateAssessmentValid({
        definition: def,
        establishmentId,
        coordinatorUserId,
        targetUsers,
        targetUserId,
      }),
    [def, establishmentId, coordinatorUserId, targetUsers, targetUserId],
  );

  return (
    <CollapseReplaceForm
      formType="create"
      title={formTitle}
      subtitle={formSubtitle}
      onClose={onClose}
      onSubmit={() => {
        if (isValid) createMutation.mutate();
      }}
      submitLabel="Générer"
      isSubmitting={createMutation.isPending}
      isValid={isValid}
      className={className}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <ClipboardList className="w-4 h-4" />
          Type de bilan
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Type"
            placeholder="Sélectionnez un type de bilan..."
            options={ASSESSMENT_TYPE_OPTIONS}
            value={type}
            onChange={(val) => {
              setType(val as AssessmentType);
              setEstablishmentId('');
              resetPersonSelections();
            }}
            required
          />
        </div>
      </div>

      {def?.establishmentRequired && (
        <div className="form-section">
          <h4 className="form-section-title">
            <Building2 className="w-4 h-4" />
            Établissement
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Autocomplete
              label="Établissement"
              placeholder="Rechercher un établissement..."
              options={establishmentOptions}
              value={establishmentId}
              onChange={(id) => {
                setEstablishmentId(id);
                resetPersonSelections();
              }}
              onSearch={onEstablishmentSearch}
              isLoading={establishmentLoading}
              required
            />
          </div>
        </div>
      )}

      {byType?.coordinator && (
        <div className="form-section">
          <h4 className="form-section-title">
            <UserCheck className="w-4 h-4" />
            {byType.coordinator.sectionTitle}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Autocomplete
              label={getRoleLabel('coordinator')}
              placeholder={scopedPersonPlaceholder(
                establishmentChosen,
                'Rechercher un coordinateur...',
              )}
              options={coordinatorOptions}
              value={coordinatorUserId}
              onChange={setCoordinatorUserId}
              onSearch={onCoordinatorSearch}
              isLoading={coordinatorLoading}
              disabled={!establishmentChosen}
              required
              helperText={byType.coordinator.helperText}
            />
          </div>
        </div>
      )}

      {def?.allowsMultiTarget && byType?.target && (
        <div className="form-section">
          <h4 className="form-section-title">
            <UserCheck className="w-4 h-4" />
            {byType.target.sectionTitle}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Autocomplete
              label={byType.target.label}
              placeholder={scopedPersonPlaceholder(establishmentChosen, byType.target.placeholder)}
              options={vscOptions}
              value=""
              onChange={(id) =>
                setTargetUsers((prev) => addAssessmentTargetUser(prev, id, vscOptions))
              }
              onSearch={onVscSearch}
              isLoading={vscLoading}
              disabled={!establishmentChosen}
              helperText={byType.target.helperText}
            />
          </div>
          {targetUsers.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {targetUsers.map((user) => (
                <span
                  key={user.id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] px-3 py-1 text-xs font-medium text-[var(--color-2-nuance-1)]"
                >
                  {user.label}
                  <button
                    type="button"
                    onClick={() =>
                      setTargetUsers((prev) => removeAssessmentTargetUser(prev, user.id))
                    }
                    className="hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {def?.targetUserRequired && !def.allowsMultiTarget && byType?.target && (
        <div className="form-section">
          <h4 className="form-section-title">
            <UserCheck className="w-4 h-4" />
            {byType.target.sectionTitle}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Autocomplete
              label={byType.target.label}
              placeholder={scopedPersonPlaceholder(establishmentChosen, byType.target.placeholder)}
              options={vscOptions}
              value={targetUserId}
              onChange={setTargetUserId}
              onSearch={onVscSearch}
              isLoading={vscLoading}
              disabled={!establishmentChosen}
              required
              helperText={byType.target.helperText}
            />
          </div>
        </div>
      )}
    </CollapseReplaceForm>
  );
};
